/*
- created by Manfred, reworked by Gregor
- generates all available SVG charts
- chart service gets container and all important meta data to generate a chart of specific type as an svg
- fires on click events to parent component

https://github.com/d3fc/d3fc
https://blog.scottlogic.com/2019/05/09/building-a-better-d3-axis.html

d3 .data key function (2nd argument) .data(data, (obj) => unique_key)
https://www.d3indepth.com/

d3 general update pattern
https://bl.ocks.org/mbostock/3808218

*/

import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { ColorManager } from '@23degrees/shared/manager/color/color.manager';
import { FormatPipe } from '@23degrees/shared/pipes/format.pipe';

// D3
import type { Selection } from 'd3-selection';
import { extent, group, max, min } from 'd3-array';
import { type ScaleBand, scaleBand, scaleLinear, scaleTime, type ScaleTime, scaleOrdinal } from 'd3-scale';
import { pointer } from 'd3-selection';
import { stack, type Series } from 'd3-shape';
import 'd3-transition';

import chroma from 'chroma-js';

import { processValueAxis, renderValueAxis, timeTicks } from './chart.axis';
import { renderGrid } from './chart.grid';
import { processLegend, renderLegend } from './chart.legend';
import {
  renderBarHorizontal,
  renderBarHorizontalGrouped,
  renderBarHorizontalStacked,
  renderBarVertical,
  renderBarVerticalGrouped,
  renderBarVerticalStacked,
} from './types/bar';

import {
  createOverlay,
  createSVG,
  setupGetTextMetrics,
  setupSplitTextToLines,
  setupSplitTextToWidth,
  setupTrimTextToWidth,
  toMoment,
  updateOverlay,
  updateSVG,
  uuid,
} from './chart.utils';

import { MESSAGE_RECIPIENTS, REQUEST_MESSAGES, RESPONSE_MESSAGES } from '@config';
import { BreakPointManager } from '../breakpoint/breakpoint.manager';
import {
  DotTypes,
  GroupedOrStackedTypes,
  ShouldAggregateTypes,
  isHorizontalType,
  isPieType,
  isTimeType,
  isVerticalType,
} from '@23degrees/shared-code/config/constants';
import { getOldFields } from '@23degrees/shared-code/config/dimension.config';
import { convertQuillToString, getQuillObject } from '@23degrees/shared-code/content/content-utilities';
import { getValueRange } from '@23degrees/shared-code/data/pipeline';
import type { Content, FontFormat, Quill } from '@23degrees/shared-code/models';
import { cloneObject, roundTo, unique } from '@23degrees/shared-code/utilities';
import { objToMoment } from '@23degrees/shared-code/utilities/date';
import { EventBus } from '@23degrees/shared/bus/event/event.bus';
import { GlobalService } from '@23degrees/shared/services/global';
import { QuillRendererService } from '@23degrees/shared/services/quill-renderer/quill-renderer.service';
import { StyleService } from '@23degrees/shared/services/style/style.service';
import { TrackerService } from '@23degrees/shared/services/tracker/tracker.service';
import { ModalOverlayService } from '@23degrees/ui/molecules/modal/modal-overlay.service';

import { processLabelAxisQuill, processOrdinalScale, renderLabelAxisQuill } from './chart.labelAxis';
import { renderArea } from './types/area';
import { renderDonut } from './types/donut';
import { renderLine } from './types/line';
import { renderPlot } from './types/dots';

export type DataRow = Record<string, unknown>;

export type DataRowExt = DataRow & {
  '~meta': {
    id: string;
    moment?: ReturnType<typeof objToMoment>;

    labelStr: string;
    labelWidth: number;
    labelHeight: number;

    labelWidthUpdated?: number;
    labelHeightUpdated?: number;

    dataLabelStr: string;
    dataLabelWidth: number;
    dataLabelHeight: number;

    dataLabelDonutStr: string;
    dataLabelDonutWidth: number;
    dataLabelDonutHeight: number;

    htmlStr: string;
    valueWidth: number;
    valueHeight: number;

    rawDataLabel: number;
    offsets: ReturnType<ChartManager['getMarginPaddingSizeInPx']>;

    minWidth: number;
    minHeight: number;

    totalWidth: number;
    totalHeight: number;
  };
};

export type DataStack = {
  '~meta': {
    data: DataRowExt[];
    group: string;
    moment?: ReturnType<typeof objToMoment>;
  };
  values: {
    key: string;
    group: string;
    value: number;
    actualObj: DataRowExt;
  }[];
};

export type PieArcExt = {
  midAngle: number;
  innerRadius: number;
  outerRadius: number;

  minWidth: number;
  maxHeight: number;

  widthInside: number;
  heightInside: number;

  widthOutside: number;
  heightOutside: number;

  isAbove: boolean;
  isRightSide: boolean;
  isStartAbove: boolean;
  isStartRightSide: boolean;

  isEndAbove: boolean;
  isEndRightSide: boolean;

  isStartInDifferentQuadrant: boolean;
  isEndInDifferentQuadrant: boolean;

  classOutside: {
    grow: boolean;
    reverse: boolean;
    left: boolean;
    right: boolean;
  };

  [key: string]: unknown;
};

// hex transitions suck, alpha would need svg fill #rrggbb and fill-opacity #a
const color2css = (color) => chroma(color).hex();

const fixLineHeight = (obj: any) => {
  if (typeof obj === 'number') return obj;
  return 1.25;
};

const logTimeCache = {};
export const logTime = (key, info?) => {
  if (!key) return;
  if (!logTimeCache[key] || !info) {
    logTimeCache[key] = new Date().valueOf();
    return;
  }

  const dateNow = new Date().valueOf();
  console.log(dateNow - logTimeCache[key], info);
  logTimeCache[key] = new Date().valueOf();
};

@Injectable()
export class ChartManager extends BaseClass {
  formatPipe = inject(FormatPipe);
  private colorManager = inject(ColorManager);
  private breakPointManager = inject(BreakPointManager);
  eventBus = inject(EventBus);
  messageBus = inject(MessageBus);
  quillRenderer = inject(QuillRendererService);
  styleService = inject(StyleService);
  modalOverlay = inject(ModalOverlayService);
  trackerService = inject(TrackerService);
  globalService = inject(GlobalService);

  /************************************************************
    PRIVATE VARIABLES
  ************************************************************/
  // SVG containers
  private header: any;
  public chart: {
    id: string;
    width: number;
    height: number;
    element: Selection<SVGSVGElement, unknown, null, undefined>;
  };
  public legend: {
    id: string;
    width: number;
    height: number;
    element: Selection<SVGSVGElement, unknown, null, undefined>;
  };
  public legendOverlay: {
    id: string;
    width: number;
    height: number;
    element: Selection<HTMLDivElement, unknown, null, undefined>;
  };
  public overlay: {
    id: string;
    width: number;
    height: number;
    element: Selection<HTMLDivElement, unknown, null, undefined>;
  };
  private footer: any;

  // chart content / data
  public data: DataRowExt[];
  public dataGroup: [string, DataRowExt[]][];
  public dataPlot: DataStack[];
  public dataStackPositive: Series<DataStack, string>[];
  public dataStackNegative: Series<DataStack, string>[];

  public content: Content;
  public range: [number, number];
  public colorScale: any;
  public groupedColorScale: any;

  private displayScale = 1;

  // uuid v4
  private id: string;

  // config
  public lineBreakFactor = 3.33;

  // scales
  public rangeMin: number;
  public rangeMax: number;

  public xDomain: unknown[];
  public xDomainTime: any;
  public yDomain: unknown[];
  public yDomainTime: any;

  public xScale: any;
  public xScaleTime: ScaleTime<number, number>;
  public yScale: any;
  public yScaleTime: ScaleTime<number, number>;
  public yScaleOrdinal: any;
  public groupScales: { [key: string]: ScaleBand<string> } = {};
  public groupScalesOrdinals: { [key: string]: any } = {};

  // widths, heights
  public chartWidth: number;
  public chartHeight: number;

  public chartWidthOffset: number;
  public chartHeightOffset: number;

  public containerWidth: number;
  public containerHeight: number;

  public totalHeight: number; // For dynamic height

  private legendContainerWidth: number;
  private legendContainerHeight: number;

  public yAxisWidth: number;
  public xAxisHeight: number;

  public xAxisHeightQuill: number;

  public maxLabelWidth: number;
  public maxLabelHeight: number;

  public dataLabelWidth: number;
  public dataLabelHeight: number;
  public dataLabelOffset = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  };

  private measureContainer: ShadowRoot | HTMLDivElement;

  // misc
  public textRotate = false;
  public labelTextMaxWidth = 0;
  public valueTextMaxWidth = 0;

  public labelAvailableHeight = 0;
  public labelAvailableHeights: number[] = [];

  // Dots
  public dotLaneHeight = 30; // Initial Dot lane
  public dotFirstLabelHeight = 0;
  public dotFirstLabelOffset = 10;
  public dotFirstLabelsHTML: any[] = [];

  // Horizontal types labels and items (Bars|Dots)
  public horizontalItems: any[] = [];

  public labelRotationDegrees = 0;
  public valueRotationDegrees = 0;
  public labelOffsetArray: any = [];
  public valueOffsetArray: any = [];

  public isValid = true;

  private isTooltipOpen = false;
  private hoverTooltipRef: unknown = null;

  @Inject(DOCUMENT) public document: Document = inject(DOCUMENT);

  public getAction = new BehaviorSubject({
    item: null,
    position: null,
    eventType: null,
  });

  /**
   * [calculate textMetrics for given font size & font]
   * @param  text         [text to be calculated]
   * @param  fontSize?    [default 13]
   * @param  fontString?  [default 'Work Sans', sans-serif]
   * @return TextMetrics  [Object with width, ...]
   */
  public getTextMetrics = setupGetTextMetrics(this.document);

  /**
   * [trim text to given length]
   * @param  text         [text to be trimmed]
   * @param  width        [text max width]
   * @param  fontSize?    [default 13]
   * @param  fontString?  [default 'Work Sans', sans-serif]
   * @return TextMetrics  [Object with width, ...]
   */
  public trimTextToWidth = setupTrimTextToWidth(this.getTextMetrics);

  public splitTextToLines = setupSplitTextToLines(this.getTextMetrics);

  public splitTextToWidth = setupSplitTextToWidth(this.getTextMetrics);

  public shouldAggregate = (type: string): boolean => ShouldAggregateTypes.includes(type);

  public isGroupedOrStacked = (type: string): boolean => GroupedOrStackedTypes.includes(type);

  private receiveDisplayScale = (msg) => {
    const { slug, displayScale } = msg;
    // if (this.content && this.content.slug === slug) {
    this.displayScale = displayScale;
    // }
  };

  constructor() {
    super();

    this.messageBus.on('CHART_LABELS_ADJUST', MESSAGE_RECIPIENTS.Chart, this.adjustLabels, this.componentDestroyed$);
    this.messageBus.on(RESPONSE_MESSAGES.DisplayScale, MESSAGE_RECIPIENTS.Chart, this.receiveDisplayScale, this.componentDestroyed$);
    this.messageBus.on('TOOLTIP_CLOSED', MESSAGE_RECIPIENTS.ToolTip, this.tooltipClosed, this.componentDestroyed$);

    const containerStyle = {
      // display: 'contents',
      position: 'absolute',
      visibility: 'hidden',
      zIndex: -1,
    };

    const contentStyle = {
      display: 'flex',
      flexDirection: 'column',
      // position: 'absolute',
      // hyphens: 'auto',
      // overflowWrap: 'break-word',
      // wordBreak: 'manual',
    };

    const childStyle = {
      margin: 0,
      padding: 0,
    };

    this.styleService.updateStyleSheet('.measure-quill-container-shadow', containerStyle);
    this.styleService.updateStyleSheet('.measure-quill-container', containerStyle);
    this.styleService.updateStyleSheet('.measure-quill-container > div', contentStyle);
    this.styleService.updateStyleSheet('.measure-quill-container p', childStyle);
  }

  ngOnDestroy() {
    this.destroyMeasureContainer();
  }

  /************************************************************
    PUBLIC FUNCTIONS
    WILL BE CALLED FROM CHART COMPONENT
  ************************************************************/
  // LAYOUT v2

  public createChartContainer = (nativeElement: HTMLDivElement): void => {
    if (this.chart) return;

    if (!this.id) this.id = uuid();
    this.chart = createSVG(`chart-${this.id}`, nativeElement);

    this.chart.element.append('g').attr('class', 'grid');
    this.chart.element.append('g').attr('class', 'chart');
    this.chart.element.append('g').attr('class', 'grid-foreground');
    this.chart.element.append('g').attr('class', 'text');
    this.chart.element.append('g').attr('class', 'voronoi');
    this.chart.element.append('g').attr('class', 'labelAxis');
    this.chart.element.append('g').attr('class', 'valueAxis');

    this.overlay = createOverlay(`chart-overlay overlay-${this.id}`, nativeElement);
    this.overlay.element.append('div').attr('class', 'overlay-values');
    this.overlay.element.append('div').attr('class', 'overlay-labelAxis');
    this.overlay.element.append('div').attr('class', 'overlay-valueAxis');
    this.overlay.element.append('div').attr('class', 'overlay-images');
  };

  public destroyChartContainer = (): void => {
    this.chart = undefined;
  };

  public createLegendContainer = (nativeElement: HTMLDivElement): void => {
    if (!this.id) this.id = uuid();
    this.legend = createSVG(`legend-${this.id}`, nativeElement);
    this.legendOverlay = createOverlay(`overlay legend-html-${this.id}`, nativeElement);
    this.legendOverlay.element.append('div').attr('class', 'overlay-legend');
  };

  public destroyLegendContainer = (): void => {
    // this.legend = undefined;
  };

  public updateChartContainerSize = (width: number, height: number): any => {
    const realHeight = height;

    this.chartWidth = width;
    this.chartHeight = height;

    this.containerWidth = width;
    this.containerHeight = height;

    this.chartWidthOffset = 0;
    this.chartHeightOffset = 0;

    if (!this.chart || !this.content) return;

    const { typeSpecific, type } = this.content;
    const { chart } = typeSpecific;
    const { dynamicHeight } = chart;

    const scrollHeight = this.processChartHeight(width, height);

    const offsetTop = this.getOffset('top');
    const offsetBottom = this.getOffset('bottom');

    this.chartHeight = scrollHeight;
    this.containerHeight = scrollHeight;

    // if (height > 0 && scrollHeight > height) {
    //   this.chartWidth -= 20;
    //   this.containerWidth -= 20;
    // }

    // console.log('updateChartSize', this.containerWidth, this.containerHeight, 'scroll', scrollHeight);

    updateSVG(this.chart, roundTo(this.containerWidth), roundTo(this.containerHeight));
    updateOverlay(this.overlay, roundTo(this.containerWidth), roundTo(this.containerHeight));

    // In case of bars.scale being 'fixed', add offsets to svg
    if (dynamicHeight && isHorizontalType(type)) {
      // this.totalHeight = Math.floor(this.containerHeight) + Math.floor(offsetTop) + Math.floor(offsetBottom);
      // this.chart.element.attr('height', this.totalHeight);
      // this.overlay.element.style('height', `${this.totalHeight}px`);
    }

    this.processScales();
    this.enrichData();

    if (isHorizontalType(type)) {
      processLabelAxisQuill(this);

      processValueAxis(this);

      this.chartWidthOffset = 0;
      processLabelAxisQuill(this);

      processOrdinalScale(this, true);
    } else {
      processLabelAxisQuill(this);

      processValueAxis(this);

      this.chartHeightOffset = 0;
      processLabelAxisQuill(this);
    }

    this.updateDataLabelSpace();

    return this.totalHeight > realHeight;
  };

  public updateLegendContainerSize = (width: number, height: number): any => {
    if (!this.legend || !this.content) return 0;

    const { typeSpecific } = this.content;
    const { legend } = typeSpecific.chart;

    if (!legend.enabled) return 0;

    const legendPreview = processLegend(this, width);
    updateSVG(this.legend, roundTo(width), roundTo(legendPreview.height));

    this.legendContainerWidth = roundTo(width);
    this.legendContainerHeight = roundTo(legendPreview.height);

    return legendPreview.height;
  };

  public getStyleInPx = (style: any, which: string, isLegend: boolean = false): number => {
    let sizeFactor = 1;
    if (this.content) {
      if (!this.content.typeSpecific.layout.oldLayoutStyle) {
        sizeFactor = this.breakPointManager.getSizeFactor(this.content.slug);
      }
    }

    let relative = 0;
    if (isLegend) {
      if (this.legendContainerWidth && this.legendContainerHeight) {
        relative = which === 'left' || which === 'right' ? this.legendContainerWidth : this.legendContainerHeight;
      }
    } else {
      if (this.containerWidth && this.containerHeight) {
        relative = which === 'left' || which === 'right' ? this.containerWidth : this.containerHeight;
      }
    }

    let size = style[which];
    if (style[`${which}Unit`] === 'em') size *= 16;
    if (style[`${which}Unit`] === 'rem') size *= 16;
    if (style[`${which}Unit`] === '%') size *= relative / 100;
    return size * sizeFactor;
  };

  public getMarginPaddingSizeInPx = (style: FontFormat, customScale?: number) => {
    const margin = { top: 0, right: 0, bottom: 0, left: 0 };
    const padding = { top: 0, right: 0, bottom: 0, left: 0 };

    let sizeFactor = 1;
    if (this.content) {
      if (!this.content.typeSpecific.layout.oldLayoutStyle) {
        sizeFactor = this.breakPointManager.getSizeFactor(this.content.slug);
      }
    }

    const { fontScale } = this.content.typeSpecific.chart;

    const relativeFactor = 1 - sizeFactor;
    const chartSizeFactor = 1 - relativeFactor * (customScale || fontScale);
    const fontSize = style.size * chartSizeFactor;

    const convertUnit = (area: any, side: string) => {
      const relative = side === 'left' || side === 'right' ? this.containerWidth : this.containerHeight;
      let size = area[side];
      if (area[`${side}Unit`] === 'em') size *= fontSize; // 16
      if (area[`${side}Unit`] === 'rem') size *= 16;
      if (area[`${side}Unit`] === '%') size *= relative / 100;
      return size * sizeFactor;
    };

    // top right bottom left
    margin.top = convertUnit(style.margin, 'top');
    margin.right = convertUnit(style.margin, 'right');
    margin.bottom = convertUnit(style.margin, 'bottom');
    margin.left = convertUnit(style.margin, 'left');

    padding.top = convertUnit(style.padding, 'top');
    padding.right = convertUnit(style.padding, 'right');
    padding.bottom = convertUnit(style.padding, 'bottom');
    padding.left = convertUnit(style.padding, 'left');

    return { margin, padding, size: fontSize };
  };

  public updateContentData = (content: any, data: any, range: any, width: number): void => {
    this.isValid = true;

    this.content = content;
    this.data = data;
    this.range = range;

    this.groupScales = {};

    this.messageBus.emit(REQUEST_MESSAGES.DisplayScale, [MESSAGE_RECIPIENTS.Chart], { slug: content.slug }, content.localStorage.referenceID);

    const { chart } = this.content.typeSpecific;
    chart.lab = chart.lab ?? {}; // BAD

    this.processData(width);

    if (this.id && this.chart) {
      processLegend(this, width);
    }
  };

  public updateChart = (): void => {
    if (!this.content || !this.chart || !this.isValid) return;
    this.renderChart();
  };

  public updateLegend = (): void => {
    if (!this.content || !this.legend) return;
    renderLegend(this);
  };

  private createShadowMeasureContainer = () => {
    const shadowElement = this.document.createElement('div');
    shadowElement.classList.add('measure-quill-container-shadow');
    this.document.body.appendChild(shadowElement);

    const container = this.document.createElement('div');
    container.className = 'measure-quill-container';

    const content = this.document.createElement('div');
    container.appendChild(content);

    const shadowRoot = shadowElement.attachShadow({ mode: 'open' });
    shadowRoot.appendChild(container);
    this.measureContainer = shadowRoot;
  };

  private createMeasureContainer = () => {
    const container = this.document.createElement('div');
    container.className = 'measure-quill-container';
    container.id = `measure-${uuid()}`;

    const containerStyle = {
      display: 'inline-block',
      position: 'absolute',
      visibility: 'hidden',
      hyphens: 'auto',
      zIndex: -1,
      margin: 0,
      padding: 0,
    };

    Object.assign(container.style, containerStyle);

    const childDiv = this.document.createElement('div');
    container.appendChild(childDiv);

    this.measureContainer = container;

    this.document.body.appendChild(container);
  };

  private destroyMeasureContainer = () => {
    const container = this.measureContainer as HTMLDivElement;
    if (container) {
      this.document.querySelector(`#${container.id}`).remove();
      this.measureContainer = undefined;
    }
  };

  public getElementRect = (
    node,
    contentWidth?,
    rotateContent = false,
    breakWord = false,
    removeChild = true,
    canOverflow = true,
    shrinkContent = false,
    extraClasses: string[] = [],
    debug = false,
  ) => {
    let container: HTMLDivElement;

    // if(!!this.document.head.attachShadow){
    //   // SHADOW DOM STYLE
    //   // ---- START -----
    //   if (!this.measureContainer) {
    //     this.setMeasureContainer();
    //   }
    //   const shadowRoot = this.measureContainer;
    //   container = shadowRoot.children[0] as HTMLElement;
    //       // ---- END -----

    // }
    // else{
    // REGULAR DOM STYLE
    // ---- START -----
    if (!this.measureContainer) {
      this.createMeasureContainer();
    }

    container = this.measureContainer as HTMLDivElement;
    // ---- END -----

    // }

    const hasFixedWidth = contentWidth !== undefined;
    const childDiv = this.measureContainer.children[0] as HTMLElement;

    // container.style.width = contentWidth ? 'min-content' : 'max-content';
    container.style.maxWidth = hasFixedWidth ? `${contentWidth}px` : undefined;
    // container.style.display = hasFixedWidth ? 'unset' : 'flex';

    for (const className of extraClasses) {
      childDiv.classList.add(className);
    }

    // container.style.display = rotateContent ? 'inline-grid' : 'inline-grid';
    childDiv.style.writingMode = rotateContent ? 'vertical-rl' : 'initial';
    // childDiv.style.overflowWrap = breakWord ? 'normal' : 'initial';
    childDiv.style.width = shrinkContent ? 'min-content' : canOverflow ? 'max-content' : 'initial';
    // childDiv.style.flex = '0 1';

    childDiv.style.hyphens = hasFixedWidth ? 'manual' : undefined;
    childDiv.style.wordBreak = hasFixedWidth ? 'normal' : undefined;
    childDiv.style.overflowWrap = hasFixedWidth ? 'break-word' : undefined;

    // childDiv.style.display = hasFixedWidth ? 'inline' : 'flex';

    if (typeof node === 'object' && 'tagName' in node) {
      childDiv.innerHTML = '';
      childDiv.appendChild(node);
    } else {
      childDiv.innerHTML = node;
    }

    if (debug) {
      container.style.zIndex = '999';
      childDiv.style.visibility = 'visible';
      childDiv.style.outline = '#ea2c12 solid 1px';
    }

    const { clientWidth, clientHeight } = childDiv;
    const rect = childDiv.getBoundingClientRect();
    const width = Math.ceil(rect.width);
    const height = Math.ceil(rect.height);

    // console.log(content.textContent, width, height, 'maxWidth', contentWidth);

    for (const className of extraClasses) {
      childDiv.classList.remove(className);
    }

    if (removeChild) {
      // container.parentNode.removeChild(container);
    }

    return { rect, width, height, clientWidth, clientHeight };
  };

  /************************************************************
    PRIVATE FUNCTIONS
    WILL BE CALLED FROM CHART SERVICE
  ************************************************************/

  public getOffset = (orientation: 'top' | 'right' | 'bottom' | 'left') => {
    const { margin, padding } = this.content.typeSpecific.chart.style;
    const offset = this.getStyleInPx(margin, orientation) + this.getStyleInPx(padding, orientation) + this.dataLabelOffset[orientation];
    // console.log(
    //   'offset',
    //   orientation,
    //   offset,
    //   'margin',
    //   this.getStyleInPx(margin, orientation),
    //   'padding',
    //   this.getStyleInPx(padding, orientation),
    //   'dataLabelOffset',
    //   this.dataLabelOffset[orientation],
    // );
    return offset;
  };

  public updateChartWidth = (relativeWidth: number = 0): void => {
    if (Number.isNaN(relativeWidth)) return;

    const { type, typeSpecific } = this.content;

    // In case od fixed height
    const offsetTop = this.getOffset('top');

    const offsetLeft = this.getOffset('left');
    const offsetRight = this.getOffset('right');

    this.chartWidthOffset += relativeWidth;

    this.chartWidth = this.containerWidth - this.chartWidthOffset - offsetLeft - offsetRight;
    this.yAxisWidth = this.chartWidthOffset;

    // console.log('updateChartWidth', this.chartWidth, '+/-', relativeWidth, 'yAxisWidth', this.yAxisWidth);

    if (this.xScale) {
      this.xScale.range([0, this.chartWidth]);

      if (isVerticalType(type)) {
        for (const obj of Object.values(this.groupScales)) {
          obj.range([0, this.xScale.bandwidth()]);
        }
      }
    }

    if (this.xScaleTime) {
      this.xScaleTime.range([0, this.chartWidth - this.xScaleBandWidth()]);
    }

    let marginLeft: number = roundTo(this.yAxisWidth + offsetLeft);

    // Do not add extra offset when is pieType, because there is no y-Axis
    if (isPieType(type)) {
      marginLeft = roundTo(offsetLeft);
    }

    const marginTop: number = roundTo(offsetTop);

    if (Number.isNaN(marginLeft) || Number.isNaN(marginTop)) return;

    this.chart.element.select('.grid').attr('transform', `translate(${marginLeft}, ${marginTop})`);
    this.chart.element.select('.chart').attr('transform', `translate(${marginLeft}, ${marginTop})`);
    this.chart.element.select('.grid-foreground').attr('transform', `translate(${marginLeft}, ${marginTop})`);
    this.chart.element.select('.text').attr('transform', `translate(${marginLeft}, ${marginTop})`);
    this.chart.element.select('.voronoi').attr('transform', `translate(${marginLeft}, ${marginTop})`);

    this.overlay.element.select('.overlay-legend').style('transform', `translate(${marginLeft}px, ${marginTop}px)`);
    this.overlay.element.select('.overlay-values').style('transform', `translate(${marginLeft}px, ${marginTop}px)`);
    this.overlay.element.select('.overlay-labelAxis').style('transform', `translate(${marginLeft}px, ${marginTop}px)`);
    this.overlay.element.select('.overlay-valueAxis').style('transform', `translate(${marginLeft}px, ${marginTop}px)`);
    this.overlay.element.select('.overlay-images').style('transform', `translate(${marginLeft}px, ${marginTop}px)`);
  };

  public updateChartHeight = (relativeHeight: number = 0): void => {
    if (Number.isNaN(relativeHeight)) return;
    const { typeSpecific } = this.content;
    const { chart } = typeSpecific;
    const { dynamicHeight } = chart;

    const offsetTop = this.getOffset('top');
    const offsetBottom = this.getOffset('bottom');

    const offsetLeft = this.getOffset('left');

    this.chartHeightOffset += relativeHeight;
    this.chartHeight = this.containerHeight - this.chartHeightOffset - offsetTop - offsetBottom;

    this.xAxisHeight = this.chartHeightOffset;

    // console.log('updateChartHeight', this.chartHeight, '+/-', relativeHeight, 'xAxisHeight', this.xAxisHeight, 'container', this.containerHeight);

    const { type } = this.content;

    if (this.yScale) {
      if (isHorizontalType(type)) {
        this.yScale.range([0, this.chartHeight - chart.bars.paddingOuterPx + offsetTop + offsetBottom]);
        // In case of relativeHeight, applied to the SVG root element

        // this.totalHeight = Math.floor(this.containerHeight) + Math.floor(relativeHeight) + Math.floor(offsetTop) + Math.floor(offsetBottom);
        // this.chart.element.attr('height', this.totalHeight);
        // this.overlay.element.style('height', `${this.totalHeight}px`);

        // console.log("🚀 ~ this.chartHeight:", this.chartHeight)
        // console.log("🚀 ~ this.totalHeight:", this.totalHeight)
        // console.log("🚀 ~ this.containerHeight:", this.containerHeight)
      } else this.yScale.range([0, this.chartHeight]);
      if (isHorizontalType(type)) {
        for (const obj of Object.values(this.groupScales)) {
          obj.range([0, this.yScale.bandwidth()]);
        }
      }
    }

    let marginLeft: number = roundTo(this.yAxisWidth + offsetLeft);

    // Do not add extra offset when is pieType, because there is no y-Axis
    if (isPieType(this.content.type)) {
      marginLeft = roundTo(offsetLeft);
    }

    const marginTop: number = roundTo(offsetTop);
    if (Number.isNaN(marginLeft) || Number.isNaN(marginTop)) return;

    this.chart.element.select('.grid').attr('transform', `translate(${marginLeft}, ${marginTop})`);
    this.chart.element.select('.chart').attr('transform', `translate(${marginLeft}, ${marginTop})`);
    this.chart.element.select('.grid-foreground').attr('transform', `translate(${marginLeft}, ${marginTop})`);
    this.chart.element.select('.text').attr('transform', `translate(${marginLeft}, ${marginTop})`);
    this.chart.element.select('.voronoi').attr('transform', `translate(${marginLeft}, ${marginTop})`);

    this.overlay.element.select('.overlay-legend').style('transform', `translate(${marginLeft}px, ${marginTop}px)`);
    this.overlay.element.select('.overlay-values').style('transform', `translate(${marginLeft}px, ${marginTop}px)`);
    this.overlay.element.select('.overlay-labelAxis').style('transform', `translate(${marginLeft}px, ${marginTop}px)`);
    this.overlay.element.select('.overlay-valueAxis').style('transform', `translate(${marginLeft}px, ${marginTop}px)`);
    this.overlay.element.select('.overlay-images').style('transform', `translate(${marginLeft}px, ${marginTop}px)`);
  };

  public getDynamicFontSize = (obj: FontFormat, width?: number, customScale?: number): any => {
    const { fontScale } = this.content.typeSpecific.chart;
    const sizeFactor = this.breakPointManager.getSizeFactor(this.content.slug);

    // only apply 50% of sizeFactor to svg / in-chart values
    const relativeFactor = 1 - sizeFactor;
    const chartSizeFactor = 1 - relativeFactor * (customScale || fontScale);

    return obj.size * chartSizeFactor;
  };

  public animationStart = () => {
    this.getAction.next({
      item: null,
      position: null,
      eventType: 'animationStart',
    });
  };

  public animationStop = () => {
    this.getAction.next({
      item: null,
      position: null,
      eventType: 'animationDone',
    });
  };

  private tooltipClosed = (referenceId) => {
    if (referenceId !== this.content?.localStorage?.referenceID) return;

    this.isTooltipOpen = false;
    this.hoverTooltipRef = null;
  };

  public mouseClick = (event: MouseEvent, dataRef: Record<string, any>, coords?: [number, number]) => {
    if (!this.chart?.element) return;

    const position = coords || pointer(event, this.chart.element.node().parentElement);
    this.eventBus.mouseClick.next({
      slug: this.content.slug,
      context: { event, dataRef, position },
    });
    // console.log('mouseClick', event, dataRef, position);
  };

  public mouseEnter = (event: MouseEvent, dataRef: Record<string, unknown>, coords?: [number, number]) => {
    if (!this.chart?.element) return;

    const position = coords || pointer(event, this.chart.element.node().parentElement);
    this.eventBus.mouseEnter.next({
      slug: this.content.slug,
      context: { event, dataRef, position },
    });
    // console.log('mouseEnter', event, dataRef, position);

    if (this.isTooltipOpen) return;

    if (this.hoverTooltipRef !== dataRef) {
      this.hoverTooltipRef = dataRef;

      if (!coords) {
        position[0] *= 1 / this.displayScale;
        position[1] *= 1 / this.displayScale;
      }

      this.messageBus.emit(
        'CHART_CLICK',
        [MESSAGE_RECIPIENTS.Chart],
        {
          context: dataRef,
          position: { x: position[0], y: position[1] },
          container: this.chart.element.node(),
          event,
          hover: true,
        },
        this.content.localStorage.referenceID,
      );
    }
  };

  public mouseLeave = (event: MouseEvent, dataRef: Record<string, unknown>, coords?: [number, number]) => {
    if (!this.chart?.element) return;

    const position = coords || pointer(event, this.chart.element.node().parentElement);
    this.eventBus.mouseLeave.next({
      slug: this.content.slug,
      context: { event, dataRef, position },
    });
    // console.log('mouseLeave', event, dataRef, position);

    if (this.isTooltipOpen) return;

    if (this.hoverTooltipRef === dataRef) {
      this.hoverTooltipRef = null;
      this.messageBus.emit('TOOLTIP_CLOSE', [MESSAGE_RECIPIENTS.ToolTip], {}, this.content.localStorage.referenceID);
    }
  };

  public mouseMove = (event: MouseEvent, dataRef: Record<string, unknown>, coords?: [number, number]) => {
    if (!this.chart?.element) return;

    const position = coords || pointer(event, this.chart.element.node().parentElement);
    this.eventBus.mouseMove.next({
      slug: this.content.slug,
      context: { event, dataRef, position },
    });
    // console.log('mouseMove', event, dataRef, position);

    if (this.isTooltipOpen) return;

    if (this.hoverTooltipRef) {
      if (!coords) {
        position[0] *= 1 / this.displayScale;
        position[1] *= 1 / this.displayScale;
      }

      this.messageBus.emit(
        'CHART_MOVE',
        [MESSAGE_RECIPIENTS.Chart],
        {
          position: { x: position[0], y: position[1] },
          container: this.chart.element.node(),
          event,
        },
        this.content.localStorage.referenceID,
      );
    }
  };

  public openPopup = (event: any, obj: any, coords?: [number, number], eventType = 'click'): void => {
    if (!this.chart?.element) return;

    const position = coords || pointer(event, this.chart.element.node().parentElement);
    if (!obj || !position) return;

    if (!coords) {
      position[0] *= 1 / this.displayScale;
      position[1] *= 1 / this.displayScale;
    }

    const { advancedTooltip, hoverTooltip } = this.content.typeSpecific;
    const advTT = getQuillObject(advancedTooltip.content);
    const ttHasText = convertQuillToString(advancedTooltip.content).trim().length > 0;
    const ttHasOther = advTT?.ops?.find((op) => typeof op.insert === 'object');
    if (!ttHasText && !ttHasOther) return;

    this.isTooltipOpen = true;

    this.getAction.next({
      item: obj,
      position: { x: position[0], y: position[1] },
      eventType,
    });

    this.messageBus.emit(
      'CHART_CLICK',
      [MESSAGE_RECIPIENTS.Chart],
      {
        context: obj,
        position: { x: position[0], y: position[1] },
        container: this.chart.element.node(),
        event,
        hover: false,
      },
      this.content.localStorage.referenceID,
    );
  };

  private closePopup = (): void => {
    this.getAction.next({
      item: null,
      position: null,
      eventType: null,
    });
  };

  public getBandwidth = () => {
    if (this.xScale?.bandwidth) return this.xScale.bandwidth();
    if (this.yScale?.bandwidth) return this.yScale.bandwidth();
    return 0;
  };

  public xScaleBandWidth = (includePadding: boolean = false): number => {
    if (!this.xScale.bandwidth) {
      return 0;
    }

    const width = this.xScale.bandwidth();
    const padding = this.xScale.paddingInner();
    return width + (includePadding ? width * padding * 2 : 0);
  };

  public yScaleBandWidth = (includePadding: boolean = false): number => {
    if (!this.yScale.bandwidth) return 0;
    const width = this.yScale.bandwidth();
    const padding = this.yScale.paddingInner();
    return width + (includePadding ? width * padding * 2 : 0);
  };

  /************************************************************
    RENDER FUNCTIONS
    HEADER, LEGEND, CHART, FOOTER
    WILL BE CALLED FROM CHART SERVICE
  ************************************************************/

  /**
   * [render chart to d3 context]
   * Entrypoint that needs to be expanded for new chartTypes
   *
   */
  private renderChart = (): void => {
    const { type } = this.content;

    this.chartWidth = this.containerWidth; // this.chart.width;
    this.chartHeight = this.containerHeight; // this.chart.height;

    this.chartWidthOffset = 0;
    this.chartHeightOffset = 0;

    this.processScales();
    if (!this.isValid) return;

    // if (this.content.typeSpecific.chart.lab?.quillValueStyle) {
    //   const format = this.styleService.convertFontFormat(this.content.typeSpecific.chart.lab.quillValueStyle);
    //   const { margin, padding } = format;
    //   // format.margin = '0';
    //   // format.padding = '0';
    //   this.styleService.updateStyleSheet('.overlay-values .quill-content', format);
    //   // this.styleService.updateStyleSheet('.overlay-values span p', { margin, padding });
    // }

    this.enrichData();

    if (isHorizontalType(type)) {
      processLabelAxisQuill(this);

      processValueAxis(this);

      this.chartWidthOffset = 0;
      processLabelAxisQuill(this);

      processOrdinalScale(this);
    } else {
      processLabelAxisQuill(this);

      processValueAxis(this);

      this.chartHeightOffset = 0;
      processLabelAxisQuill(this);
    }

    this.updateDataLabelSpace();

    renderValueAxis(this);
    // renderValueAxisQuill(this);

    // renderLabelAxis(this);
    renderLabelAxisQuill(this);

    renderGrid(this);

    this.animationStart();

    if (['barchart', 'horizontalbarchart', 'groupedbarchart', 'stackedbarchart', 'bar-grouped-horizontal', 'bar-stacked-horizontal'].includes(type)) {
      this.chart.element.style('shape-rendering', 'crispedges');
    } else {
      this.chart.element.style('shape-rendering', 'unset');
    }

    if (type === 'barchart') renderBarVertical(this);
    if (type === 'groupedbarchart') renderBarVerticalGrouped(this);
    if (type === 'stackedbarchart') renderBarVerticalStacked(this);
    if (type === 'horizontalbarchart') renderBarHorizontal(this);
    if (type === 'bar-grouped-horizontal') renderBarHorizontalGrouped(this);
    if (type === 'bar-stacked-horizontal') renderBarHorizontalStacked(this);
    if (type === 'areachart') renderArea(this);
    if (type === 'linechart') renderLine(this);
    if (type === 'donutchart') renderDonut(this);
    if (type === 'parliamentchart') renderDonut(this);
    if (type === 'piechart') renderDonut(this);
    if (type === 'ttcharts-linechart') renderLine(this);
    if (type === 'x-plot') renderPlot(this);
  };

  /************************************************************
    CHART HELPER FUNCTIONS
    WILL BE CALLED FROM CHART SERVICE
  ************************************************************/

  public xPos = (value: number): number => this.xScale(value);
  public yPos = (value: number): number => this.yScale(value);

  public verticalWidth = (type: string, aggregation?: string): number =>
    type === 'groupedbarchart' ? this.groupScales[aggregation].bandwidth() : this.xScaleBandWidth();
  public verticalHeight = (value: number): number => Math.abs(this.yScale(0) - this.yScale(value));
  public horizontalWidth = (value: number): number => Math.abs(this.xScale(0) - this.xScale(value));
  public horizontalHeight = (type: string, aggregation?: string): number =>
    type === 'bar-grouped-horizontal' ? this.groupScales[aggregation].bandwidth() : this.yScaleBandWidth();

  public verticalSpace = (value: number): number => {
    return value < 0 ? this.chartHeight - this.yScale(0) : this.yScale(Math.max(0, this.rangeMin));
    // if (this.content.typeSpecific.chart.values.position.includes('outside')) {
    //   return value < 0 ? this.chartHeight - this.yScale(0) + this.dataLabelOffset.bottom : this.yScale(0) + this.dataLabelOffset.top;
    // } else {
    //   return value < 0 ? this.chartHeight - this.yScale(0) : this.yScale(0);
    // }
  };

  public horizontalSpace = (value: number): number => {
    return value >= 0 ? this.chartWidth - this.xScale(0) : this.xScale(0);
    // if (this.content.typeSpecific.chart.values.position.includes('outside')) {
    //   return value >= 0 ? this.chartWidth - this.xScale(0) + this.dataLabelOffset.right : this.xScale(0) + this.dataLabelOffset.left;
    // } else {
    //   return value >= 0 ? this.chartWidth - this.xScale(0) : this.xScale(0);
    // }
  };

  public isTextInside = (textWidth, textHeight, areaWidth, areaHeight, isRotated: boolean = false): boolean => {
    if (isRotated) {
      return textWidth < areaHeight && textHeight < areaWidth;
    }

    return textWidth < areaWidth && textHeight < areaHeight;
  };

  /************************************************************
    PROCESSING FUNCTIONS
    WILL BE CALLED FROM CHART SERVICE
  ************************************************************/

  /**
   * [process all input data]
   * - create colorScale
   * - create grouped data for area / line
   * - create stacked data for stacked
   * @param  void
   * @return void
   */
  private processData = (width: number): void => {
    const { type, typeSpecific } = this.content;
    const { labelField, valueField } = getOldFields(this.content.type, this.content?.typeSpecific?.dimensions);
    const { colors2 } = typeSpecific.style;
    const { fieldName } = colors2;

    this.colorScale = this.colorManager.getColorScale(colors2, this.data, this.range); // this.styleService.createColorScale(colors, this.data, valueField);

    if (this.shouldAggregate(type)) {
      this.dataGroup = Array.from(group(this.data, (obj) => obj[labelField] as string));

      const colorGroup = [];
      this.dataGroup = this.dataGroup.map((obj: any, idx: number) => {
        const groupValue = idx * (100 / Math.max(1, this.dataGroup.length - 1));
        // for color generation
        obj['~meta'] = {
          [fieldName]: groupValue,
          [labelField]: obj[0],
          // [valueField]: groupValue
        };

        colorGroup.push({
          [fieldName]: groupValue,
          [labelField]: obj[0],
          // [valueField]: groupValue
        });

        return obj;
      });

      this.groupedColorScale = this.colorManager.getColorScale(colors2, colorGroup, [0, 100], false); // this.styleService.createColorScale(colors, colorGroup, valueField);
    }
  };

  private processChartHeight = (width: number, height: number): number => {
    const { type, typeSpecific } = this.content;
    const { bars, dynamicHeight, dots } = typeSpecific.chart;
    const { colors2 } = typeSpecific.style;
    const { labelAxis } = typeSpecific.chart.axis;

    let updatedHeight = height;
    const { titleStyle, valueStyle } = labelAxis;
    const { aggregationField, labelField, valueField } = getOldFields(this.content.type, this.content?.typeSpecific?.dimensions);
    const mapField = this.shouldAggregate(type) ? aggregationField : labelField;

    // Dots first label
    if (DotTypes.includes(type) && dots.firstLabel.enabled) {
      let firstLabelHeight = 0;
      this.dotFirstLabelsHTML = [];
      const row = {};
      for (const group of this.dataGroup) {
        row[labelField] = group[0];
        const value = group[1][0][valueField];
        const aggregation = group[1][0][aggregationField];
        // useDataColor
        let color = dots.firstLabel.style.color;
        if (dots.firstLabel.style.useDataColor) {
          color = colors2.categorical.values.find((item) => item.value === group[0])?.color;
        }
        const firstLabelStyle = cloneObject(dots.firstLabel.style);
        firstLabelStyle.size = this.getDynamicFontSize(dots.firstLabel.style);
        const firstLabel = this.getHTMLandRect(dots.firstLabel.quill, firstLabelStyle, row, this.content, true);
        this.dotFirstLabelsHTML.push({
          ...firstLabel,
          value,
          aggregation,
          color,
        });
        firstLabelHeight = Math.max(firstLabelHeight, firstLabel.height);
      }
      this.dotFirstLabelHeight = firstLabelHeight;
    }

    let labelsAboveBarsHeight = 0;
    if (labelAxis.labelsAboveBars) {
      const dynamicValueStyle = cloneObject(labelAxis.valueStyle);
      dynamicValueStyle.size = this.getDynamicFontSize(labelAxis.valueStyle, undefined, 1);

      const oneLineHeight = dynamicValueStyle.size + dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);

      const valueMarginTop = this.getStyleInPx(dynamicValueStyle.margin, 'top');
      const valueMarginBottom = this.getStyleInPx(dynamicValueStyle.margin, 'bottom');

      labelsAboveBarsHeight = oneLineHeight + valueMarginTop + valueMarginBottom;
    }

    // Calculate how much space is needed having min and max Heights
    let minHeight = 0; // DynamicHeight 'fixed'
    let hugHeight = 0; // DynamicHeight 'hug'

    if (['horizontalbarchart'].includes(type)) {
      minHeight = this.data.length * (bars.minHeight + labelsAboveBarsHeight);
      hugHeight = this.data.length * (bars.height + labelsAboveBarsHeight + bars.paddingInnerPx);
      // updatedHeight = Math.max(updatedHeight, this.data.length * (bars.minHeight + extraHeight));
    }

    if (['bar-grouped-horizontal'].includes(type) && this.dataGroup[0]) {
      minHeight = this.data.length * (bars.minHeight + labelsAboveBarsHeight);
      hugHeight = this.data.length * (bars.height + labelsAboveBarsHeight + bars.paddingInnerPx);
      // updatedHeight = Math.max(updatedHeight, this.data.length * bars.minHeight + this.dataGroup[0][1].length * extraHeight);
    }

    if (['bar-stacked-horizontal'].includes(type) && this.dataGroup[0]) {
      // Check if dataGroup items have all the same length
      const isHomogenous = this.dataGroup.every((sub) => sub[1].length === this.dataGroup[0][1].length);
      let dataLength = 0;
      if (isHomogenous) {
        dataLength = this.dataGroup[0][1].length;
      } else {
        const labelData = unique(this.data.map((dataRow) => dataRow[mapField]));
        dataLength = labelData.length;
      }

      minHeight = dataLength * (bars.minHeight + labelsAboveBarsHeight);
      hugHeight = dataLength * (bars.height + labelsAboveBarsHeight + bars.paddingInnerPx);
      // updatedHeight = Math.max(updatedHeight, this.dataGroup[0][1].length * (bars.minHeight + extraHeight));
    }

    if (dynamicHeight && isHorizontalType(type)) {
      // NOTE: In case of scale being fixed, height  is the whole series + outter padding - 1x the innerPadding (for the last element)
      // updatedHeight = hugHeight + bars.paddingOuterPx - bars.paddingInnerPx;
      if (['bar-grouped-horizontal'].includes(type)) {
        // const groupPaddingInnerPx = (this.data.length / this.dataGroup.length) * bars.groupPaddingInnerPx;
        // const groupPaddingOuterPx = (this.data.length / this.dataGroup.length) * bars.groupPaddingOuterPx * 2;
        // updatedHeight += groupPaddingInnerPx + bars.groupPaddingOuterPx * 2;
      }
    } else {
      updatedHeight = Math.max(updatedHeight, minHeight);
    }

    return updatedHeight;
  };

  private adjustLabels = (msg) => {
    const { type, typeSpecific } = this.content;
    const { labelAxis } = typeSpecific.chart.axis;
    const { labelField, aggregationField } = getOldFields(this.content.type, this.content?.typeSpecific?.dimensions);

    if (!this.xScaleTime) return;
    // let tickLabels = this.xScaleTime.ticks(labelAxis.numLabels);

    let tickValues = [];
    const mapField = this.shouldAggregate(type) ? aggregationField : labelField;
    const axisValues = unique(this.data.map((obj) => obj[mapField]).filter((obj) => obj !== null));
    if (axisValues.length < 1) return;

    if (msg.type === 'increase') {
      labelAxis.tickInput = (labelAxis.tickCount ?? labelAxis.tickInput ?? 7) + 1;
      tickValues = timeTicks(axisValues, labelAxis.tickInput);

      // console.log('inc', labelAxis.tickInput, tickValues);

      while (tickValues.length < labelAxis.tickInput && labelAxis.tickInput < axisValues.length) {
        labelAxis.tickInput++;
        tickValues = timeTicks(axisValues, labelAxis.tickInput);
      }

      labelAxis.tickCount = tickValues.length;
    }

    if (msg.type === 'decrease') {
      labelAxis.tickInput = (labelAxis.tickCount ?? labelAxis.tickInput ?? 7) - 1;
      tickValues = timeTicks(axisValues, labelAxis.tickInput);

      // console.log('dec', labelAxis.tickInput, tickValues);

      while (tickValues.length < labelAxis.tickInput && labelAxis.tickInput < axisValues.length) {
        labelAxis.tickInput--;
        tickValues = timeTicks(axisValues, labelAxis.tickInput);
      }

      labelAxis.tickCount = tickValues.length;
    }

    this.renderChart();

    // console.log('adjustLabels', msg.type, labelAxis.tickCount, labelAxis.tickInput);
  };

  private processScales = (): void => {
    const { type, typeSpecific } = this.content;
    const { bars, grid, values, scaleZero, dynamicHeight, waterfall } = typeSpecific.chart;
    const { labelField, valueField, timeField, aggregationField } = getOldFields(this.content.type, this.content?.typeSpecific?.dimensions);
    const { labelAxis } = typeSpecific.chart.axis;

    this.rangeMin = this.range ? this.range[0] : +min(this.data, (obj: any) => obj[valueField]);
    this.rangeMax = this.range ? this.range[1] : +max(this.data, (obj: any) => obj[valueField]);

    const { valueRange } = typeSpecific.data;
    if (!['user', 'auto'].includes(valueRange.type) && (scaleZero || type !== 'linechart')) {
      if (this.rangeMin > 0) this.rangeMin = 0;
      if (this.rangeMax < 0) this.rangeMax = 0;
    }

    if (this.rangeMin === 0 && this.rangeMax === 0) {
      this.rangeMax = 10;
    }

    // tagesschau fix
    if (this.content.slug === 'injected-line-chart?') {
      this.rangeMin = Math.floor(this.range[0]);
    }

    if (isVerticalType(type) || isTimeType(type)) this.processScaleVertical();
    if (isHorizontalType(type)) this.processScaleHorizontal();

    if (typeSpecific.chart.flip) {
      this.xDomain.reverse();
      this.yDomain.reverse();
    }

    if (this.isGroupedOrStacked(type) && aggregationField) {
      let groupLength = 0;
      const groups = group(this.data, (obj) => obj[aggregationField] as string);

      for (const dataGroup of groups.values()) {
        const labels = dataGroup.map((obj) => obj[labelField] as string);
        groupLength = Math.max(groupLength, labels.length);
      }

      for (const [key, dataGroup] of groups) {
        const groupLabels = dataGroup.map((obj) => obj[labelField] as string);
        for (let i = 0; i < groupLength; i++) {
          if (groupLabels.length === groupLength) break;
          groupLabels.push(`~pad_${i}`);
        }

        const bandWidth = isHorizontalType(type) ? this.yScaleBandWidth() : this.xScaleBandWidth();

        this.groupScales[key] = scaleBand().domain(groupLabels).range([0, bandWidth]).paddingInner(bars.groupPaddingInner).paddingOuter(bars.groupPaddingOuter);
      }
    }

    // Stack Values
    let isValidArea = true;
    if (['areachart', 'stackedbarchart', 'bar-stacked-horizontal'].includes(type)) {
      const stackField = type === 'areachart' ? timeField : aggregationField;
      const stackKeys = unique(this.data.map((obj) => obj[stackField] as string));
      const labelKeys = unique(this.data.map((obj) => obj[labelField] as string));

      const stackDataPositive: DataStack[] = [];
      const stackDataNegative: DataStack[] = [];
      for (const stackKey of stackKeys) {
        // reduce group to key: value
        const stackGroup = this.data.filter((dataObj) => dataObj[stackField] === stackKey);
        if (!stackGroup) continue;
        // if (type === 'areachart' && stackGroup.length < 2) continue;

        // make sure the objects are in the right order
        stackGroup.sort((left, right) => (left[labelField] as string).localeCompare(right[labelField] as string));

        // reduce group to key: value
        const dataPositive = stackGroup.reduce((acc, cur) => {
          const value = cur[valueField] as number;
          if (value > 0) {
            acc[cur[labelField] as string] = Math.max(0, value);
          }

          return acc;
        }, {} as DataStack);

        const dataNegative = stackGroup.reduce((acc, cur) => {
          const value = cur[valueField] as number;
          if (value < 0) {
            acc[cur[labelField] as string] = Math.min(0, value);
          }

          return acc;
        }, {} as DataStack);

        dataPositive['~meta'] = {
          data: stackGroup,
          group: stackKey,
        };

        dataNegative['~meta'] = {
          data: stackGroup,
          group: stackKey,
        };

        if (timeField) {
          dataPositive['~meta'].moment = stackGroup[0]['~meta'].moment;
          dataNegative['~meta'].moment = stackGroup[0]['~meta'].moment;
        }

        // make sure there are no empty values
        for (const key of labelKeys) {
          if (!dataPositive[key]) dataPositive[key] = 0;
          if (!dataNegative[key]) dataNegative[key] = 0;
        }

        stackDataPositive.push(dataPositive);
        stackDataNegative.push(dataNegative);
      }

      // isValidArea = !(type === 'areachart' && stackData.length === 0);

      const stackFn = stack<DataStack>().keys(labelKeys);
      this.dataStackPositive = stackFn(stackDataPositive);
      this.dataStackNegative = stackFn(stackDataNegative);

      // const { valueRange } = typeSpecific.data;
      if (!['user', 'auto'].includes(valueRange.type)) {
        let rangeMin = 0;
        let rangeMax = 0;
        for (let idx = 0; idx < stackKeys.length; idx++) {
          const stackMin = this.dataStackNegative.reduce((acc, val) => Math.min(acc, Number.isNaN(val[idx][1]) ? 0 : val[idx][1]), 0);
          const stackMax = this.dataStackPositive.reduce((acc, val) => Math.max(acc, Number.isNaN(val[idx][1]) ? 0 : val[idx][1]), 0);

          rangeMin = Math.min(rangeMin, stackMin);
          rangeMax = Math.max(rangeMax, stackMax);
        }

        if (waterfall.enabled) {
          const [min, max] = getValueRange(this.content, this.data);
          rangeMin = Math.min(0, min);
          rangeMax = max;
        }

        if (isHorizontalType(type)) {
          this.xDomain = [rangeMin, rangeMax];
          this.xScale.domain([rangeMin, rangeMax]);
          if (typeSpecific.chart.grid.enabled && typeSpecific.chart.axis.valueAxis.enabled) {
            this.xScale.nice(Number.parseInt(String(grid.xAxis), 10));
          }
        } else {
          this.yDomain = [rangeMax, rangeMin];
          this.yScale.domain([rangeMax, rangeMin]);
          if (typeSpecific.chart.grid.enabled && typeSpecific.chart.axis.valueAxis.enabled) {
            this.yScale.nice(Number.parseInt(String(grid.yAxis), 10));
          }
        }
      }
    }

    // group Values
    if (DotTypes.includes(type)) {
      const stackField = aggregationField;
      const stackKeys = unique(this.data.map((obj) => obj[stackField] as string));
      const labelKeys = unique(this.data.map((obj) => obj[labelField] as string));

      const stackData: DataStack[] = [];
      for (const stackKey of stackKeys) {
        // reduce group to key: value
        const stackGroup = this.data.filter((dataObj) => dataObj[stackField] === stackKey);
        if (!stackGroup) continue;

        // make sure the objects are in the right order
        stackGroup.sort((left, right) => (left[labelField] as string).localeCompare(right[labelField] as string));

        const data = stackGroup.reduce((acc, cur) => {
          acc[cur[labelField] as string] = cur[valueField];
          if (!acc.values) acc.values = [];

          const value = {
            key: cur[labelField] as string,
            group: cur[stackField] as string,
            value: cur[valueField] as number,
            actualObj: cur,
          };

          acc.values.push(value);
          return acc;
        }, {} as DataStack);

        data['~meta'] = {
          data: stackGroup,
          group: stackKey,
        };

        data['~meta'] = {
          data: stackGroup,
          group: stackKey,
        };

        if (timeField) data['~meta'].moment = stackGroup[0]['~meta'].moment;

        stackData.push(data);
      }

      this.dataPlot = stackData;

      if (!['user', 'auto'].includes(valueRange.type)) {
        let rangeMin = 0;
        let rangeMax = 0;

        for (let i = 0; i < this.dataPlot.length; i++) {
          const _stackMin = Math.min(...this.dataPlot[i].values.map((i) => i.value));
          const _stackMax = Math.max(...this.dataPlot[i].values.map((i) => i.value));
          rangeMin = Math.min(rangeMin, _stackMin);
          rangeMax = Math.max(rangeMax, _stackMax);
        }

        this.xDomain = [rangeMin, rangeMax];
        this.xScale.domain([rangeMin, rangeMax]);
        if (typeSpecific.chart.grid.enabled && typeSpecific.chart.axis.valueAxis.enabled) {
          this.xScale.nice(Number.parseInt(String(grid.xAxis), 10));
        }
      }
    }

    this.isValid = isValidArea;
    this.getAction.next({
      item: this.isValid,
      position: null,
      eventType: 'isValid',
    });
  };

  private processScaleVertical = (): void => {
    // bar, grouped, stacked?
    const { type, typeSpecific } = this.content;
    const { labelField, aggregationField } = getOldFields(this.content.type, this.content?.typeSpecific?.dimensions);
    const { bars, grid, dynamicHeight } = typeSpecific.chart;
    const mapField = this.shouldAggregate(type) ? aggregationField : labelField;
    const { dataType, format } = typeSpecific.fields[mapField];

    const { margin, padding } = typeSpecific.chart.style;
    const offsetTop = this.getOffset('top'); // this.getStyleInPx(margin, 'top') + this.getStyleInPx(padding, 'top');
    const offsetBottom = this.getOffset('bottom'); // this.getStyleInPx(margin, 'bottom') + this.getStyleInPx(padding, 'bottom');

    const offsetLeft = this.getOffset('left'); // this.getStyleInPx(margin, 'left') + this.getStyleInPx(padding, 'left');
    const offsetRight = this.getOffset('right'); // this.getStyleInPx(margin, 'right') + this.getStyleInPx(padding, 'right');

    this.xDomain = unique(this.data.map((obj: any) => obj[mapField]));
    if (dataType === 'date' && isTimeType(type)) this.xDomain = extent(this.data, (obj) => toMoment(obj, this.content)); // && isTimeType(type)
    this.yDomain = [this.rangeMin, this.rangeMax];

    // 0 = bottom
    this.yDomain.reverse();

    this.xScale = scaleBand().range([0, this.chart.width - offsetLeft - offsetRight]);
    if (dataType === 'date' && isTimeType(type)) this.xScale = scaleTime().range([0, this.chart.width - offsetLeft - offsetRight]); // && isTimeType(type)
    const baseChartHeight = this.chart.height - offsetTop - offsetBottom;
    // In case of bars.scale being 'fixed', we dont want paddingOuterPx to be part of the scale
    const chartHeight = dynamicHeight && isHorizontalType(type) ? baseChartHeight - bars.paddingOuterPx : baseChartHeight;
    this.yScale = scaleLinear().range([0, chartHeight]);

    this.xScale.domain(this.xDomain);
    this.yScale.domain(this.yDomain);

    if (isTimeType(type)) {
      if (dataType !== 'date') {
        this.xScale.paddingInner(0).paddingOuter(0);
      }
    } else {
      this.xScale.paddingInner(bars.paddingInner).paddingOuter(bars.paddingOuter);
    }

    if (typeSpecific.chart.grid.enabled && typeSpecific.chart.axis.valueAxis.enabled) {
      this.yScale.nice(Number.parseInt(String(grid.yAxis), 10));
    }

    if (dataType === 'date') {
      const { inputFormat } = format.date;
      const { sorting } = typeSpecific.chart;
      this.xDomainTime = extent(this.data, (obj) => objToMoment(obj[mapField], inputFormat, format.locale));

      // this takes render pipeline area/line sorting into account
      if (sorting.enabled && sorting.sortFields[0] === mapField && sorting.sortOrder === 'desc' && !isTimeType(type)) this.xDomainTime.reverse();

      this.xScaleTime = scaleTime().range([0, this.chart.width - offsetLeft - offsetRight - this.xScaleBandWidth()]);
      this.xScaleTime.domain(this.xDomainTime);
    }
  };

  private processScaleHorizontal = (): void => {
    // bar, grouped, stacked?
    const { type, typeSpecific } = this.content;
    const { labelField, aggregationField } = getOldFields(this.content.type, this.content?.typeSpecific?.dimensions);
    const { bars, grid, dynamicHeight } = typeSpecific.chart;
    const mapField = this.shouldAggregate(type) ? aggregationField : labelField;
    const { dataType } = typeSpecific.fields[mapField];
    const { labelAxis } = typeSpecific.chart.axis;

    const { margin, padding } = typeSpecific.chart.style;
    const offsetTop = this.getOffset('top'); // this.getStyleInPx(margin, 'top') + this.getStyleInPx(padding, 'top');
    const offsetBottom = this.getOffset('bottom'); // this.getStyleInPx(margin, 'bottom') + this.getStyleInPx(padding, 'bottom');

    const offsetLeft = this.getOffset('left'); // this.getStyleInPx(margin, 'left') + this.getStyleInPx(padding, 'left');
    const offsetRight = this.getOffset('right'); // this.getStyleInPx(margin, 'right') + this.getStyleInPx(padding, 'right');

    this.xDomain = [this.rangeMin, this.rangeMax];
    this.yDomain = unique(this.data.map((obj: any) => obj[mapField]));
    if (dataType === 'date' && isTimeType(type)) this.yDomain = extent(this.data, (obj) => toMoment(obj, this.content));

    this.xScale = scaleLinear().range([0, this.chart.width - offsetLeft - offsetRight]);
    this.yScaleOrdinal = scaleOrdinal();
    this.yScaleOrdinal.domain(this.yDomain);
    this.yScale = scaleBand().range([0, this.chart.height - offsetTop - offsetBottom]);

    if (dataType === 'date' && isTimeType(type)) this.yScale = scaleTime().range([0, this.chart.width - offsetLeft - offsetRight]);

    this.xScale.domain(this.xDomain);
    this.yScale.domain(this.yDomain);

    if (isTimeType(type)) {
      if (dataType !== 'date') {
        this.yScale.paddingInner(0).paddingOuter(0);
      }
    } else {
      if (dynamicHeight && isHorizontalType(type)) {
        let extraHeight = 0;
        if (labelAxis.labelsAboveBars) {
          const dynamicValueStyle = cloneObject(labelAxis.valueStyle);
          dynamicValueStyle.size = this.getDynamicFontSize(labelAxis.valueStyle, undefined, 1);
          const oneLineHeight = dynamicValueStyle.size + dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);
          const valueMarginTop = this.getStyleInPx(dynamicValueStyle.margin, 'top');
          const valueMarginBottom = this.getStyleInPx(dynamicValueStyle.margin, 'bottom');
          extraHeight = oneLineHeight + valueMarginTop + valueMarginBottom;
        }

        // NOTE: compensation for paddingInnerPx
        // const paddingInner = bars.paddingInnerPx / (bars.height + bars.paddingInnerPx + extraHeight);
        // this.yScale.paddingInner(paddingInner); // .paddingOuter(bars.paddingOuter);
      } else {
        this.yScale.paddingInner(bars.paddingInner).paddingOuter(bars.paddingOuter);
      }
    }

    if (typeSpecific.chart.grid.enabled && typeSpecific.chart.axis.valueAxis.enabled) {
      this.xScale.nice(Number.parseInt(String(grid.xAxis), 10));
    }
  };

  public getHTMLandRect = (quillDelta, style, dataRow, content?: Content, canRotate = false, customStyle?) => {
    const REMOVE_DOM_ELEMENT = true;
    try {
      content = content ?? this.content;
      const sizeFactor: number = this.breakPointManager.getSizeFactor(content.slug);

      // TODO check if custom delta exists for this data point
      const styleClone = cloneObject(style);

      if (canRotate && isHorizontalType(content.type)) {
        // rotate 90° clockwise
        styleClone.margin.right = style.margin.top;
        styleClone.margin.rightUnit = style.margin.topUnit;
        styleClone.margin.bottom = style.margin.right;
        styleClone.margin.bottomUnit = style.margin.rightUnit;
        styleClone.margin.left = style.margin.bottom;
        styleClone.margin.leftUnit = style.margin.bottomUnit;
        styleClone.margin.top = style.margin.left;
        styleClone.margin.topUnit = style.margin.leftUnit;
      }

      const html = this.quillRenderer.convertDeltaToHTML(quillDelta, styleClone, dataRow, sizeFactor, content, customStyle);

      const { width, height } = this.getElementRect(html, undefined, undefined, undefined, REMOVE_DOM_ELEMENT);
      const empty = this.quillRenderer.deltaToString(quillDelta).length <= 1;

      return { html, width: empty ? 0 : width, height };
    } catch (error) {
      // valueStr = typeSpecific.chart.lab.quillValue;
    }
  };

  public getQuillIndex = (customQuills: Quill[], label: string) =>
    customQuills.findIndex((item) => {
      if (item.id.includes('[group]_')) {
        const key = item.id.replace('[group]_', '');
        return label.includes(`-${key}`);
      }
      return item.id === label;
    });

  public getQuillsIds = (customQuills: Quill[]) => customQuills.map((item) => item.id);

  public getQuill = (customQuills: Quill[], label: string, fallback: string) => {
    const _id = this.getQuillIndex(customQuills, label);
    let quillHTML = fallback;
    if (_id > -1) {
      const _quill = customQuills[_id];
      quillHTML = _quill.touched ? _quill.quill : fallback;
    }
    return quillHTML;
  };

  private enrichData = (): any => {
    const { type, typeSpecific } = this.content;

    const { donut, values } = typeSpecific.chart;
    const { labelAxis, valueAxis } = typeSpecific.chart.axis;
    const { labelField, valueField, timeField, aggregationField } = getOldFields(this.content.type, this.content?.typeSpecific?.dimensions);

    const mapField = this.shouldAggregate(type) ? aggregationField : labelField;
    const { dataType } = typeSpecific.fields[mapField];

    this.maxLabelWidth = 0;
    this.maxLabelHeight = 0;

    this.dataLabelWidth = 0;
    this.dataLabelHeight = 0;

    const dynamicDataLabelStyle = cloneObject(values.style);
    dynamicDataLabelStyle.size = this.getDynamicFontSize(values.style);

    const dynamicDataLabelDonutStyle = cloneObject(donut.quillStyle);
    dynamicDataLabelDonutStyle.size = this.getDynamicFontSize(donut.quillStyle);

    const dynamicLabelStyle = cloneObject(labelAxis.valueStyle);
    dynamicLabelStyle.size = this.getDynamicFontSize(labelAxis.valueStyle);

    const areaOffsets = this.getMarginPaddingSizeInPx(values.style);

    let sizeFactor: number = this.breakPointManager.getSizeFactor(this.content.slug);

    // logTime('enrichData')
    // console.time('enrichData');

    const labelData = unique(this.data.map((dataRow) => dataRow[mapField]));

    let isSpaceForLabel = true;
    let isSpaceForDataLabel = true;

    if (!isPieType(type)) {
      const labelScale = isVerticalType(type) || isTimeType(type) ? this.xScale : this.yScale;

      const bandwidth = this.getBandwidth();
      const availableWidth = (bandwidth > 0 ? bandwidth : labelScale.range()[1] / labelData.length) + 1;

      isSpaceForLabel = availableWidth >= dynamicLabelStyle.size;
      isSpaceForDataLabel = availableWidth >= dynamicDataLabelStyle.size;
    }

    if (isTimeType(type) && dataType === 'date') return;

    const labelMap = new Map();

    // console.log("values.customQuills", values.customQuills);

    // logTime('quill');
    this.textRotate = false;
    for (const dataRow of this.data) {
      const _label = dataRow[labelField] as string;
      const label = dataRow[mapField] as string;
      const group = dataRow[aggregationField] as string;
      const value = dataRow[valueField] as number;
      if (value === undefined) continue;

      dataRow['~meta'].id = aggregationField ? `${group}-${_label}` : `${_label}`;
      dataRow['~meta'].labelStr = '';
      dataRow['~meta'].labelWidth = 0;
      dataRow['~meta'].labelHeight = 0;

      dataRow['~meta'].labelWidthUpdated = undefined;
      dataRow['~meta'].labelHeightUpdated = undefined;

      dataRow['~meta'].dataLabelStr = '';
      dataRow['~meta'].dataLabelWidth = 0;
      dataRow['~meta'].dataLabelHeight = 0;

      dataRow['~meta'].dataLabelDonutStr = '';
      dataRow['~meta'].dataLabelDonutWidth = 0;
      dataRow['~meta'].dataLabelDonutHeight = 0;

      let niceValue = value; // this.formatPipe.transform(value, values.format);
      // const width = this.getTextMetrics(niceValue, dynamicStyle).width;

      if (isSpaceForLabel) {
        const customStyle = isHorizontalType(type) ? { flex: '1 1 auto' } : {};

        const labelQuillHTML = this.getQuill(labelAxis.customQuills, label, labelAxis.quillValue);
        const labelQuill = this.getHTMLandRect(labelQuillHTML, dynamicLabelStyle, dataRow, this.content, false, customStyle);

        if (labelQuill) {
          dataRow['~meta'].labelStr = labelQuill.html;
          dataRow['~meta'].labelWidth = labelQuill.width;
          dataRow['~meta'].labelHeight = labelQuill.height;

          this.maxLabelWidth = Math.max(this.maxLabelWidth, labelQuill.width);
          this.maxLabelHeight = Math.max(this.maxLabelHeight, labelQuill.height);
        }
      }

      if (isSpaceForDataLabel || values.forceDraw) {
        const __label = group ? `${group}-${_label}` : label;
        const dataLabelQuillHTML = this.getQuill(values.customQuills, __label, values.quillValue);
        const dataLabelQuill = this.getHTMLandRect(dataLabelQuillHTML, dynamicDataLabelStyle, dataRow, this.content, true);
        if (dataLabelQuill) {
          dataRow['~meta'].dataLabelStr = dataLabelQuill.html;
          dataRow['~meta'].dataLabelWidth = dataLabelQuill.width;
          dataRow['~meta'].dataLabelHeight = dataLabelQuill.height;

          this.dataLabelWidth = Math.max(this.dataLabelWidth, dataLabelQuill.width);
          this.dataLabelHeight = Math.max(this.dataLabelHeight, dataLabelQuill.height);
        }

        // TODO clean this mess
        const totalWidth = dataLabelQuill.width + areaOffsets.margin.left + areaOffsets.margin.right;
        const totalHeight = dataLabelQuill.height + areaOffsets.margin.top + areaOffsets.margin.bottom; // areaOffsets.size

        dataRow['~meta'].htmlStr = dataRow['~meta'].dataLabelStr;
        dataRow['~meta'].valueWidth = dataRow['~meta'].dataLabelWidth;
        dataRow['~meta'].valueHeight = dataRow['~meta'].dataLabelHeight;

        dataRow['~meta'].rawDataLabel = niceValue;
        dataRow['~meta'].totalWidth = totalWidth;
        dataRow['~meta'].totalHeight = totalHeight;
        dataRow['~meta'].offsets = areaOffsets;

        dataRow['~meta'].htmlStr = dataRow['~meta'].dataLabelStr;
        dataRow['~meta'].minWidth = dataRow['~meta'].dataLabelWidth;
        dataRow['~meta'].minHeight = dataRow['~meta'].dataLabelHeight;
        // TODO end

        if (isPieType(type)) {
          const donutQuillHTML = this.getQuill(labelAxis.customQuills, label, donut.quillValue);
          const dataLabelDonutQuill = this.getHTMLandRect(donutQuillHTML, dynamicDataLabelDonutStyle, dataRow, this.content, true);

          if (dataLabelDonutQuill) {
            dataRow['~meta'].dataLabelDonutStr = dataLabelDonutQuill.html;
            dataRow['~meta'].dataLabelDonutWidth = dataLabelDonutQuill.width;
            dataRow['~meta'].dataLabelDonutHeight = dataLabelDonutQuill.height;
          }
        }

        if (['barchart', 'groupedbarchart', 'stackedbarchart'].includes(type)) {
          if (dataLabelQuill.width + 1 > this.verticalWidth(type, group)) {
            this.textRotate = true;
          }
        }
      }

      // console.log(value, valueWidth, this.verticalWidth(type, group));
    }

    // logTime('quill', 'quill');

    // logTime('enrichData', 'loop');

    if (this.textRotate) {
      this.dataLabelWidth = 0;
      this.dataLabelHeight = 0;
      for (const obj of this.data) {
        // TRY PREVENTING REFLOW
        // requestAnimationFrame(() => {
        const { dataLabelStr } = obj['~meta'];
        const { width, height } = this.getElementRect(dataLabelStr, undefined, true, undefined, true);
        obj['~meta'].dataLabelWidth = width;
        obj['~meta'].dataLabelHeight = height;
        // console.log('vertical', obj['~meta'].rawDataLabel, rectInfo.width, rectInfo.height, obj['~meta'].dataLabelWidth, obj['~meta'].dataLabelHeight);

        this.dataLabelWidth = Math.max(this.dataLabelWidth, width);
        this.dataLabelHeight = Math.max(this.dataLabelHeight, height);
        // });
      }
    }

    // console.timeEnd('enrichData');
  };

  private updateDataLabelSpace = () => {
    const { type, typeSpecific } = this.content;
    const { values, bars, waterfall, dots } = typeSpecific.chart;
    const { labelField, valueField, timeField, aggregationField } = getOldFields(this.content.type, this.content?.typeSpecific?.dimensions);

    this.dataLabelOffset.top = 0;
    this.dataLabelOffset.bottom = 0;
    this.dataLabelOffset.left = 0;
    this.dataLabelOffset.right = 0;

    if (!values.enabled) return;

    const bandwidth = this.getBandwidth() + 1;
    // console.log('data label w/h', this.dataLabelWidth, this.dataLabelHeight, 'bandwidth', bandwidth);
    if ((isVerticalType(type) ? this.dataLabelWidth : this.dataLabelHeight) > bandwidth && !values.forceDraw) return;

    const hasPositive = this.data.some((dataRow) => dataRow[valueField] && (dataRow[valueField] as number) > 0);
    const hasNegative = this.data.some((dataRow) => dataRow[valueField] && (dataRow[valueField] as number) < 0);

    const maxPositive = this.data.reduce((acc, row) => Math.max(acc, (row[valueField] as number) ?? 0), 0);
    const maxNegative = this.data.reduce((acc, row) => Math.min(acc, (row[valueField] as number) ?? 0), 0);

    let allPositiveInside = true;
    let allNegativeInside = true;

    // vertical
    if (isVerticalType(type)) {
      let applyTop = false;
      let applyBottom = false;

      const fullRange = this.yScale.range()[1];

      if (type.includes('stacked')) {
        if (values.sumStacked) {
          applyTop = hasPositive;
          applyBottom = hasNegative;
        }
      } else {
        if (values.position.includes('inside')) {
          for (const dataRow of this.data) {
            const value = dataRow[valueField] as number;
            if (value === undefined) continue;

            const { dataLabelHeight } = dataRow['~meta'];

            const rangeAdjust = this.rangeMin >= 0 ? Math.min(this.rangeMin, value) : Math.min(0, value);
            const height = this.verticalHeight(value > 0 ? value - rangeAdjust : value);

            const isInside = dataLabelHeight - 1 <= height;
            const isOutside = dataLabelHeight - 1 <= fullRange - height;
            const isZero = isInside ? values.position.includes('inside-zero') : values.position.includes('alt-zero');

            if (isInside || (isOutside && !isZero)) continue;

            // value exceeds bars
            if (isZero && value < 0 && !hasPositive) applyTop = true;
            if (isZero && value >= 0 && !hasNegative) applyBottom = true;

            if (value >= 0 && !isInside) allPositiveInside = false;
            if (value < 0 && !isInside) allNegativeInside = false;
          }

          if (hasPositive && !allPositiveInside) applyTop = true;
          if (hasNegative && !allNegativeInside) applyBottom = true;
        } else {
          const isZero = values.position.includes('outside-zero');
          applyTop = (isZero && hasNegative) || (!isZero && hasPositive);
          applyBottom = (isZero && hasPositive) || (!isZero && hasNegative);
        }
      }

      const spaceTop = Math.abs(this.yScale(this.yScale.domain()[0]) - this.yScale(this.yDomain[0]));
      const spaceBottom = Math.abs(this.yScale(this.yScale.domain()[1]) - this.yScale(this.yDomain[1]));

      if (fullRange !== 0 && fullRange !== spaceTop && fullRange !== spaceBottom) {
        // approximation, bad
        const shrinkFactorTop = fullRange / (fullRange - spaceTop);
        const shrinkFactorBottom = fullRange / (fullRange - spaceBottom);

        const dataLabelSpaceTop = Math.max(0, this.dataLabelHeight - spaceTop) * shrinkFactorTop;
        const dataLabelSpaceBottom = Math.max(0, this.dataLabelHeight - spaceBottom) * shrinkFactorBottom;

        // console.log('range', fullRange, 'label width', this.dataLabelHeight);
        // console.log('applyTop', applyTop, 'applyBottom', applyBottom);
        // console.log('spaceTop', spaceTop, 'spaceBottom', spaceBottom);
        // console.log('shrinkTop', shrinkFactorTop, 'shrinkBottom', shrinkFactorBottom);
        // console.log('labelTop', dataLabelSpaceTop, 'labelBottom', dataLabelSpaceBottom);

        if (applyTop) this.dataLabelOffset.top = dataLabelSpaceTop;
        if (applyBottom) this.dataLabelOffset.bottom = dataLabelSpaceBottom;

        // 0 in case waterfall and all above zero
        if (waterfall.enabled && this.range[0] > 0) this.dataLabelOffset.bottom = 0;

        // console.log('applyTop', applyTop, 'applyBottom', applyBottom, this.dataLabelOffset);
      }

      if (applyTop || applyBottom) {
        this.updateChartHeight(0);
      }
    }

    // horizontal
    if (isHorizontalType(type)) {
      let applyLeft = false;
      let applyRight = false;

      const maxPositiveWidth = this.horizontalWidth(maxPositive);
      const maxNegativeWidth = this.horizontalWidth(maxNegative);

      const fullRange = this.xScale.range()[1];

      if (DotTypes.includes(type)) {
        for (const dataRow of this.data) {
          const value = dataRow[valueField] as number;
          if (value === undefined) continue;
          const { dataLabelWidth } = dataRow['~meta'];
          // const rangeAdjust = this.rangeMin >= 0 ? Math.min(this.rangeMin, value) : Math.min(0, value);
          // const width = this.horizontalWidth(value > 0 ? value - rangeAdjust : value);
          const scaledValue = this.xScale(value);
          const fitsOutside = value >= 0 ? scaledValue + dataLabelWidth <= fullRange : scaledValue - dataLabelWidth > 0;

          if (fitsOutside) continue;

          if (value > 0) applyRight = true;
          if (value < 0) applyLeft = true;
        }
      } else {
        if (type.includes('stacked')) {
          if (values.sumStacked) {
            applyLeft = hasNegative;
            applyRight = hasPositive;
          }
        } else {
          if (values.position.includes('inside')) {
            for (const dataRow of this.data) {
              const value = dataRow[valueField] as number;
              if (value === undefined) continue;

              const { dataLabelWidth } = dataRow['~meta'];

              const rangeAdjust = this.rangeMin >= 0 ? Math.min(this.rangeMin, value) : Math.min(0, value);
              const width = this.horizontalWidth(value > 0 ? value - rangeAdjust : value);

              const fitsInside = dataLabelWidth - 1 <= width;
              const fitsOutside = dataLabelWidth <= fullRange - width;
              const fitsMaxOutside = value >= 0 ? dataLabelWidth - 1 <= maxPositiveWidth - width : dataLabelWidth - 1 <= maxNegativeWidth - width;
              const isAway = values.position.includes('alt-away');
              const isZero = fitsInside ? values.position.includes('inside-zero') : values.position.includes('alt-zero');
              // console.log(
              //   dataRow[labelField],
              //   width,
              //   'dataLabel',
              //   dataLabelWidth,
              //   'fitsInside',
              //   fitsInside,
              //   'fitsOutside',
              //   fitsOutside,
              //   'fitsMaxOutside',
              //   fitsMaxOutside,
              //   'isAway',
              //   isAway,
              //   'isZero',
              //   isZero
              // );

              if (fitsInside || (fitsOutside && !isZero && (!isAway || fitsMaxOutside))) continue;

              // value exceeds bars
              if (isZero && value >= 0 && !hasNegative) applyLeft = true;
              if (isZero && value < 0 && !hasPositive) applyRight = true;

              if (value >= 0 && !fitsInside) allPositiveInside = false;
              if (value < 0 && !fitsInside) allNegativeInside = false;
            }

            if (hasNegative && !allNegativeInside) applyLeft = true;
            if (hasPositive && !allPositiveInside) applyRight = true;
          } else {
            const isZero = values.position.includes('outside-zero');
            applyLeft = (isZero && hasPositive) || (!isZero && hasNegative);
            applyRight = (isZero && hasNegative) || (!isZero && hasPositive);
          }
        }
      }

      const spaceLeft = Math.abs(this.xScale(this.xScale.domain()[0]) - this.xScale(this.xDomain[0]));
      const spaceRight = Math.abs(this.xScale(this.xScale.domain()[1]) - this.xScale(this.xDomain[1]));

      if (fullRange !== 0 && fullRange !== spaceLeft && fullRange !== spaceRight) {
        // approximation, bad
        const shrinkFactorLeft = fullRange / (fullRange - spaceLeft);
        const shrinkFactorRight = fullRange / (fullRange - spaceRight);

        const dataLabelSpaceLeft = Math.max(0, this.dataLabelWidth - spaceLeft) * shrinkFactorLeft;
        const dataLabelSpaceRight = Math.max(0, this.dataLabelWidth - spaceRight) * shrinkFactorRight;

        // console.log('range', fullRange, 'label width', this.dataLabelWidth);
        // console.log('applyLeft', applyLeft, 'applyRight', applyRight);
        // console.log('spaceL', spaceLeft, 'spaceR', spaceRight);
        // console.log('shrinkL', shrinkFactorLeft, 'shrinkR', shrinkFactorRight);
        // console.log('labelL', dataLabelSpaceLeft, 'labelR', dataLabelSpaceRight);

        if (applyLeft) this.dataLabelOffset.left = dataLabelSpaceLeft;
        if (applyRight) this.dataLabelOffset.right = dataLabelSpaceRight;

        // 0 in case waterfall and all above zero
        if (waterfall.enabled && this.range[0] > 0) this.dataLabelOffset.left = 0;

        // console.log('applyLeft', applyLeft, 'applyRight', applyRight, this.dataLabelOffset);
      }

      if (applyLeft || applyRight) {
        this.updateChartWidth(0);
      }
    }
  };
}
